import { useSyncStore } from "@/app/(store)/sync.store"
import { TCallerID } from "@/schemas/user.schema"
import {
  Box,
  useCombobox,
  Combobox,
  InputBase,
  Input,
  ScrollArea,
  Group,
} from "@mantine/core"
import { IconCheck } from "@tabler/icons-react"
import { useCallback, useMemo, useState } from "react"

// TODO: Support modifying the "f" number type
// TODO: Support saving the caller id selection change

interface ICallerIDSelectorProps {
  classNames?: {
    root?: string
  }
}

export function CallerIDSelector({ classNames }: ICallerIDSelectorProps) {
  const { userPreferences } = useSyncStore()

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const [value, setValue] = useState<string | null>(null)

  const getCallerIDPrettyLabel = useCallback((callerID: TCallerID | null) => {
    if (!callerID) return ""

    let label: string = ""

    if (callerID?.type === "r") {
      // It is a personal number, we want to show the
      // label or phone number and then "- Personal"
      label = `${callerID?.label || callerID?.number} - Personal`
    } else if (callerID?.type === "c") {
      // It is a company number, we want to show the
      // label or phone number and then "- Company"
      label = `${callerID?.label || callerID?.number} - Company`
    } else if (callerID?.type === "l") {
      // It is the Ring Local number, we want to show the
      // "Ring Local" string only
      label = callerID?.label ?? "Ring Local"
    } else {
      // If callerID is type "f"
      // We want to show the label or phone number only
      label = `${callerID?.label || callerID?.number}`
    }

    return label
  }, [])

  const currentCallerId = useMemo(() => {
    const selectedCallerId = (userPreferences?.callerIds ?? []).find(
      (item) => item?.selected
    )

    return selectedCallerId ?? null
  }, [userPreferences?.callerIds])

  const options = useMemo(() => {
    return (userPreferences?.callerIds ?? [])
      .filter((item) => (item?.number || item?.label) && item?.type)
      .map((item) => {
        const isActive =
          item?.number === currentCallerId?.number ||
          (item?.type === "l" && item.type === currentCallerId?.type)
        return (
          <Combobox.Option
            value={item?.number || `l`}
            key={item?.number || `l`}
            active={isActive}
            classNames={{
              option: isActive ? "!bg-r-blue/80 !text-white" : "",
            }}
          >
            <Group gap="xs">
              {isActive && <IconCheck className="size-4" />}
              {getCallerIDPrettyLabel(item)}
            </Group>
          </Combobox.Option>
        )
      })
  }, [currentCallerId, getCallerIDPrettyLabel, userPreferences])

  const findCallerIDByNumber = useCallback(
    (number: string) => {
      return (userPreferences?.callerIds ?? []).find(
        (item, index) =>
          item?.number === number ||
          (item?.type === "l" && number.startsWith("l"))
      )
    },
    [userPreferences?.callerIds]
  )

  return (
    <Box className={classNames?.root}>
      <Combobox
        store={combobox}
        onOptionSubmit={(val) => {
          setValue(val)
          combobox.closeDropdown()
        }}
        width={260}
        size="sm"
      >
        <Combobox.Target>
          <InputBase
            component="button"
            type="button"
            pointer
            rightSection={<Combobox.Chevron />}
            rightSectionPointerEvents="none"
            onClick={() => combobox.toggleDropdown()}
            size="sm"
          >
            {getCallerIDPrettyLabel(currentCallerId) || (
              <Input.Placeholder>Pick a caller ID</Input.Placeholder>
            )}
          </InputBase>
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            <ScrollArea.Autosize type="scroll" mah={200}>
              {options}
            </ScrollArea.Autosize>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Box>
  )
}
